import React, { useEffect, useState } from "react";
import { getFreshToken, supabase } from "./auth.js";
import { ui, LANG_KEY, type Lang } from "./shared.js";

const WRAP: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  minHeight: "100vh", background: "#000", color: "#fff",
  fontFamily: "'Space Mono', monospace", fontSize: 12, flexDirection: "column", gap: 16,
};
const BTN: React.CSSProperties = {
  background: "#fff", color: "#000", border: "none", padding: "10px 24px",
  fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.12em",
  textTransform: "uppercase", cursor: "pointer", marginTop: 8,
};

type AcceptStatus = "loading" | "success" | "already" | "expired" | "seat_full" | "email_mismatch" | "not_shared" | "invite_used" | "needs_login" | "error" | "no_token";

export function AcceptInvite({ inviteToken }: { inviteToken: string }) {
  const [status, setStatus] = useState<AcceptStatus>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  // UX-COPY-1: denial copy localized via the shared ui() layer (EN + ZH).
  const lang = (localStorage.getItem(LANG_KEY) as Lang) ?? "en";

  useEffect(() => {
    if (!inviteToken) { setStatus("no_token"); return; }
    let cancelled = false;
    const needLogin = () => {
      // Store current URL so the SIGNED_IN handler can redirect back after login
      localStorage.setItem("studio_pending_invite_url", window.location.href);
      setStatus("needs_login");
    };
    (async () => {
      // Use getFreshToken() (refreshes if near expiry) — NOT the raw
      // localStorage("studio_token"). A stale stored token makes the server's
      // verifyJWT fail ("Auth session missing!") → 401 BEFORE the accept logic
      // runs, so the invite never reaches the email-binding gate and no
      // folder_members row is created. This was the root of "uninvited not
      // cleanly rejected" / "invited B never became a member".
      const authToken = await getFreshToken();
      if (cancelled) return;
      if (!authToken) { needLogin(); return; }
      try {
        const r = await fetch(`/api/studio/invites/accept?token=${encodeURIComponent(inviteToken)}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const d = await r.json().catch(() => ({})) as Record<string, unknown>;
        if (cancelled) return;
        if (r.ok) { setStatus(d.already ? "already" : "success"); return; }
        // 401 = the session died between getFreshToken and this request →
        // ask the user to sign in again, don't show a generic error.
        if (r.status === 401) { needLogin(); return; }
        if (r.status === 410 || d.error === "invite_expired") { setStatus("expired"); return; }
        if (r.status === 409 && d.error === "invite_already_used") { setStatus("invite_used"); return; }
        if (r.status === 409 && d.error === "seat_limit_reached") { setStatus("seat_full"); return; }
        // F5-SEC-1: this invite was issued to a different email → clean refusal.
        if (r.status === 403 && d.error === "invite_email_mismatch") { setStatus("email_mismatch"); return; }
        // UX-COPY-1: the folder was returned to private after the invite was sent.
        if (r.status === 403 && d.error === "folder_not_shared") { setStatus("not_shared"); return; }
        setStatus("error");
        setErrorMsg(typeof d.error === "string" ? d.error : "Unknown error");
      } catch {
        if (!cancelled) { setStatus("error"); setErrorMsg("Network error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken]);

  // SECURITY GATE (founder, 2026-06-13): a mismatched invite is a BLOCK, never a
  // login entry. By this point the (wrong) account has already authenticated via
  // OAuth — if we leave that session alive, navigating to "/" drops them into
  // THEIR OWN workspace: the fallback backdoor. Force sign-out the INSTANT we hit
  // the mismatch so no workspace is reachable; SIGNED_OUT also wipes the pending-
  // invite URL, so there is no auto-resume from the wrong link. The only exit is a
  // fresh normal login. Covers all three blocked cases (wrong account + valid
  // link / correct account + wrong link / un-invited account) — they all funnel
  // through the server's single `invite_email_mismatch`.
  useEffect(() => {
    if (status === "email_mismatch") void supabase.auth.signOut().catch(() => {});
  }, [status]);

  const goHome = () => { localStorage.setItem("studio_active_tab", "shared"); window.location.href = "/"; };
  const goLogin = () => { window.location.href = "/"; };
  // Sign out (again, defensively) before leaving, so "/" renders LOGIN, not a
  // workspace, even if the mount-time sign-out is still in flight.
  const backToLogin = async () => { await supabase.auth.signOut().catch(() => {}); window.location.href = "/"; };

  if (status === "loading") return <div style={WRAP}><div style={{ color: "#666" }}>Accepting invitation…</div></div>;

  if (status === "needs_login") return (
    <div style={WRAP}>
      <div style={{ color: "#aaa", marginBottom: 4 }}>Please sign in to accept this invitation.</div>
      <button style={BTN} onClick={goLogin}>Sign in</button>
    </div>
  );

  if (status === "success" || status === "already") return (
    <div style={WRAP}>
      <div style={{ color: "#5ae09a", fontFamily: "'Rama Gothic E', system-ui", fontSize: "0.8rem", letterSpacing: "0.2em" }}>
        {status === "already" ? "ALREADY ACCEPTED" : "INVITATION ACCEPTED"}
      </div>
      <div style={{ color: "#888", fontSize: 11 }}>
        {status === "already" ? "You already have access to this folder." : "You now have access to the shared folder."}
      </div>
      <button style={BTN} onClick={goHome}>Open Studio</button>
    </div>
  );

  if (status === "expired") return (
    <div style={WRAP}>
      <div style={{ color: "#e0a055" }}>{ui("invite_expired", lang)}</div>
      <button style={BTN} onClick={goHome}>Go to Studio</button>
    </div>
  );

  if (status === "invite_used") return (
    <div style={WRAP}>
      <div style={{ color: "#e0a055" }}>{ui("invite_used", lang)}</div>
      <button style={BTN} onClick={goHome}>Go to Studio</button>
    </div>
  );

  if (status === "not_shared") return (
    <div style={WRAP}>
      <div style={{ color: "#e0a055" }}>{ui("denial_folder_unshared", lang)}</div>
      <button style={BTN} onClick={goHome}>Go to Studio</button>
    </div>
  );

  if (status === "seat_full") return (
    <div style={WRAP}>
      <div style={{ color: "#e0a055" }}>Seat limit reached.</div>
      <div style={{ color: "#666", fontSize: 11 }}>This folder is at capacity. Ask the owner to free up a seat.</div>
      <button style={BTN} onClick={goHome}>Go to Studio</button>
    </div>
  );

  // Single message, single exit — NO workspace-reachable button (the backdoor).
  if (status === "email_mismatch") return (
    <div style={WRAP}>
      <div style={{ color: "#e0a055" }}>{ui("invite_not_for_account", lang)}</div>
      <button style={BTN} onClick={backToLogin}>{ui("back_to_login", lang)}</button>
    </div>
  );

  return (
    <div style={WRAP}>
      <div style={{ color: "#e05555" }}>Could not accept invitation.</div>
      <div style={{ color: "#666", fontSize: 11 }}>{errorMsg}</div>
      <button style={BTN} onClick={goHome}>Go to Studio</button>
    </div>
  );
}
